import React from 'react';
import SeoHead from '../components/SeoHead';
import { getOptimizedImageUrl } from '../lib/cms';

export default function HotstoneMassagePage() {
  return (
    <>
      <SeoHead
        title="Hotstone-massage – Niels Boye | Ibjerget på Midtfyn"
        description="Hotstonemassage v. Niels Boye på Ibjerget. Dybdegående afspænding og selvforkælelse med varme lavasten, massageolie og healing."
        canonicalUrl="https://www.ibjerget.dk/hotstone-massage"
      />

      {/* Hero */}
      <section className="relative pt-32 pb-20 sm:pt-40 sm:pb-28 bg-[#381d6e] text-white overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-30">
          <img
            src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/1df9f3fb-cd3d-49d9-8f1a-8a59416f1b4e/ibjerget-hotstone-massage.jpg', { width: 1400, quality: 80, fit: 'cover' })}
            alt="Hotstone massage med varme vulkanske sten på Ibjerget"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#381d6e] via-[#381d6e]/75 to-transparent" />

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#d9238c]/30 border border-[#d9238c]/40 text-[#f7eefa] text-xs font-semibold uppercase tracking-wider mb-6">
            <i className="fa-solid fa-fire-flame-curved" aria-hidden="true"></i>
            <span>Terapeut Niels Boye</span>
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl font-bold text-[#faf5f9] tracking-tight leading-tight">
            Hotstonemassage
          </h1>
          <p className="mt-6 text-base sm:text-xl text-[#f7eefa] max-w-2xl mx-auto leading-relaxed font-normal">
            Dybdegående afslapning og absolut selvforkælelse med varme vulkanske lavasten.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <a
              href="#booking"
              className="px-8 py-4 rounded-full bg-[#d9238c] hover:bg-[#be1878] text-white font-semibold text-sm shadow-md transition-colors"
            >
              Book tid hos Niels
            </a>
            <a
              href="tel:66157902"
              className="px-8 py-4 rounded-full bg-white/10 hover:bg-white/20 text-white font-semibold text-sm border border-white/20 transition-colors whitespace-nowrap"
            >
              <i className="fa-solid fa-phone mr-2 text-[#f7eefa]"></i>
              Ring 66 15 79 02
            </a>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 sm:py-24 bg-[#faf5f9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {/* Left: Text Content */}
            <div className="lg:col-span-7 space-y-6">
              <span className="text-xs font-semibold uppercase tracking-widest text-[#d9238c] block">
                Om massagen
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-[#1f142e] tracking-tight">
                Varme lavasten og dyb afspænding
              </h2>

              <div className="prose prose-stone text-[#4e3568] text-base leading-relaxed space-y-4">
                <p>
                  Denne massage giver dyb afslapning og er absolut selvforkælelse.
                </p>
                <p>
                  Det er en behandling, der på en yderst behagelig måde giver dig ro både i kroppen og i dit sind. Det er fantastisk, hvilken virkning de varme sten har på kroppens muskler. Det kan virke overraskende, hvad der sker i kroppen, når de varme vulkansten bliver brugt som massagesten. Kroppen bliver udrenset, lymfevæsken i kroppen bliver aktiveret, og blodet pulserer mere aktivt igennem alle muskler. Til sidst lægges de varme sten ned ad ryggen, og du ligger og får en dyb afspænding.
                </p>
              </div>

              {/* Selvforkælelse, Hvorfor, Hvordan */}
              <div className="pt-4 space-y-5">
                <div className="bg-white p-6 rounded-2xl border border-[#edd5e8] shadow-sm">
                  <h3 className="font-serif text-lg font-semibold text-[#1f142e] mb-2 flex items-center gap-2">
                    <i className="fa-solid fa-spa text-[#d9238c]" aria-hidden="true"></i>
                    <span>Selvforkælelse</span>
                  </h3>
                  <p className="text-sm text-[#4e3568] leading-relaxed">
                    Du får en massage med de varme lavasten, der giver dyb afspænding i musklerne - og dermed også dit sind, så stress og spændinger opløses og erstattes af en indre ro og velvære.
                  </p>
                </div>

                <div className="bg-white p-6 rounded-2xl border border-[#edd5e8] shadow-sm">
                  <h3 className="font-serif text-lg font-semibold text-[#1f142e] mb-2 flex items-center gap-2">
                    <i className="fa-solid fa-circle-question text-[#d9238c]" aria-hidden="true"></i>
                    <span>Hvorfor</span>
                  </h3>
                  <p className="text-sm text-[#4e3568] leading-relaxed">
                    Vi mennesker har det med at "gemme" oplevelser i kroppen, specielt dem, vi ikke syntes har været så sjove. Det skaber manglende generel energi til rådighed, fordi musklerne til en vis grad blokeres af de gemte oplevelser/traumer, så der skal bruges mere energi i kroppen end nødvendigt. Når musklerne masseres - og specielt når de masseres med de varme lavasten - har dette psykiske materiale mulighed for at frigøres. Det kan betyde, at du dagen efter massagen kan føle dig mere træt (du kan også kalde det afslappet), eller du får drømme, der handler om det materiale, der frigøres. Alt sammen en proces, der hjælper dig til mere generel energi i dit liv.
                  </p>
                </div>

                <div className="bg-white p-6 rounded-2xl border border-[#edd5e8] shadow-sm">
                  <h3 className="font-serif text-lg font-semibold text-[#1f142e] mb-2 flex items-center gap-2">
                    <i className="fa-solid fa-clock text-[#d9238c]" aria-hidden="true"></i>
                    <span>Hvordan</span>
                  </h3>
                  <p className="text-sm text-[#4e3568] leading-relaxed">
                    Massagen med olie og varme sten varer ca. 50 minutter og bliver efterfulgt af 15 minutter, hvor du ligger med varme sten på ryggen og slapper dybt af mens du får healing. I alt 65 minutter.
                  </p>
                </div>
              </div>

            </div>

            {/* Right: Existing Genre Photo & Practical Info */}
            <div className="lg:col-span-5 space-y-8">
              
              {/* Photo: ibjerget-hotstone-massage.jpg */}
              <div className="rounded-3xl overflow-hidden shadow-lg border border-[#edd5e8] bg-white">
                <img
                  src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/1df9f3fb-cd3d-49d9-8f1a-8a59416f1b4e/ibjerget-hotstone-massage.jpg', { width: 800, quality: 85, fit: 'cover' })}
                  alt="Varme vulkanske massagesten"
                  className="w-full h-auto object-cover"
                />
                <div className="p-4 bg-[#fcf8fb] text-xs text-[#9b1b66] text-center border-t border-[#edd5e8]">
                  Varme vulkansten giver musklerne dyb afspænding og forløsning
                </div>
              </div>

              {/* Praktisk information */}
              <div id="booking" className="bg-[#381d6e] text-[#faf5f9] rounded-3xl p-8 border border-[#5f2a99] shadow-lg space-y-6">
                <div>
                  <span className="text-xs font-semibold uppercase tracking-widest text-[#e895c8] block mb-1">
                    Fakta & Booking
                  </span>
                  <h3 className="font-serif text-2xl font-semibold text-white">
                    Praktisk information
                  </h3>
                </div>

                <div className="space-y-3.5 text-sm">
                  <div className="flex items-center justify-between pb-2 border-b border-[#5f2a99]">
                    <span className="text-[#f7eefa]">Behandler:</span>
                    <span className="font-semibold text-white">Massør og terapeut Niels Boye</span>
                  </div>
                  <div className="flex items-center justify-between pb-2 border-b border-[#5f2a99]">
                    <span className="text-[#f7eefa]">Varighed:</span>
                    <span className="font-semibold text-white">Ca. 65 minutter</span>
                  </div>
                  <div className="flex items-center justify-between pb-2 border-b border-[#5f2a99]">
                    <span className="text-[#f7eefa]">Pris:</span>
                    <span className="font-semibold text-white">500 kr.</span>
                  </div>
                  <div className="flex items-center justify-between pb-2 border-b border-[#5f2a99]">
                    <span className="text-[#f7eefa]">Sted:</span>
                    <span className="font-semibold text-white text-right">Ibjergvej 12, 5792 Årslev</span>
                  </div>
                  <div className="flex items-center justify-between pb-2 border-b border-[#5f2a99]">
                    <span className="text-[#f7eefa]">Afbud:</span>
                    <span className="font-semibold text-white">Senest 24 timer før</span>
                  </div>
                </div>

                {/* Booking til Niels Boye */}
                <div className="pt-4 border-t border-[#5f2a99] space-y-4">
                  <div className="space-y-1">
                    <p className="text-xs uppercase tracking-wider text-[#e895c8] font-semibold">
                      Behandler
                    </p>
                    <p className="text-lg font-serif font-bold text-white">
                      Niels Boye
                    </p>
                    <p className="text-xs text-[#f7eefa]">
                      Kontakt Niels direkte for tidsbestilling og spørgsmål:
                    </p>
                  </div>

                  <div className="flex flex-col gap-3 pt-2">
                    <a
                      href="tel:66157902"
                      className="flex items-center justify-center gap-3 w-full py-3.5 px-5 rounded-full bg-[#d9238c] hover:bg-[#be1878] text-white font-semibold text-sm shadow transition-colors"
                    >
                      <i className="fa-solid fa-phone" aria-hidden="true"></i>
                      <span>Ring på 66 15 79 02</span>
                    </a>
                    <a
                      href="mailto:niels@ibjerget.dk"
                      className="flex items-center justify-center gap-3 w-full py-3 px-5 rounded-full bg-white/10 hover:bg-white/20 text-white font-semibold text-sm border border-white/20 transition-colors"
                    >
                      <i className="fa-regular fa-envelope" aria-hidden="true"></i>
                      <span>Skriv til niels@ibjerget.dk</span>
                    </a>
                  </div>

                  <div className="p-3 bg-[#4a2282] rounded-xl text-xs text-[#f7eefa] leading-relaxed">
                    <p>
                      <strong>Bemærk:</strong> Niels er Elektro Hyper Sensitiv, og derfor skal mobiltelefon og eventuel smartwatch være slukket under behandlingen.
                    </p>
                  </div>
                </div>

              </div>

            </div>

          </div>
        </div>
      </section>
    </>
  );
}
